import React, { FormEvent, useEffect, useState } from 'react';

import { IParameter } from '../../../types/parameter';
import { IMetaParameter, ParameterType } from '../../../types/meta_paramter';

import useUpdateParameter from '../../../hooks/parameter/useUpdateParameter';
import useCreateParameter from '../../../hooks/parameter/useCreateParameter';
import useDeleteParameter from '../../../hooks/parameter/useDeleteParameter';

import { isEmptyCustom } from '../../../utils/type';

import { SVGIcon } from '../../wrappers/SVGIcon';

import { Icons } from '../../../data/Assets';

interface IParameterProps {
    parameter: IParameter;
    currOption: number[];
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isDelete: boolean;
    relatedMetaParameters: IMetaParameter[];
    setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
};

const Parameter = ({ parameter, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, relatedMetaParameters, setIsResetNew, setDeleteModalMessage }: IParameterProps) => {
    const [title, setTitle] = useState<string>(parameter.id ? parameter.name : 'New Parameter');

    const [currentParameter, setCurrentParameter] = useState<IParameter>(parameter);

    const [parameterValue, setParameterValue] = useState<any | any[]>('');
    const [isArray, setIsArray] = useState<boolean>(false);

    const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
    const [, setIsServiceError] = useState<string | null>(null); //eslint-disable-line
    // const { data: metaFunctions, isPending, isError } = useGetMetaFunctions();

    useEffect(() => {
        const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentParameter.name);
        if (!metaParameter) return;

        if (currentParameter.value) {
            setParameterValue(metaParameter.type === ParameterType.ARRAY ? JSON.parse(currentParameter.value) : currentParameter.value);
        } else {
            if (metaParameter.type === ParameterType.ARRAY) {
                setParameterValue(['']);
            } else {
                setParameterValue('');
            }
        }
    }, [currentParameter.value]); //eslint-disable-line

    useEffect(() => {
        setCurrentParameter(parameter);
        setTitle(parameter.id ? parameter.name : 'New Parameter');

        try {
            const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentParameter.name);
            if (!metaParameter) return;

            const value = JSON.parse(parameter.value);
            if (metaParameter.type === ParameterType.ARRAY) {
                if (!Array.isArray(value) || !value?.length) addValueField();
            }

            setIsArray(Array.isArray(value));
            setParameterValue(value);
        } catch (error) { };
    }, [parameter]); // eslint-disable-line

    const addValueField = () => {
        const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentParameter.name);
        if (!metaParameter) return;

        if (metaParameter.type === ParameterType.ARRAY) {
            if (!isArray || !parameterValue?.length) {
                setIsArray(true);
                setParameterValue(['']);
            } else {
                setParameterValue(['', ...parameterValue]);
            }
        }
    }

    const { mutate: updateParameter } = useUpdateParameter({ setIsProcessRunning, setIsServiceError });
    const { mutate: createParameter } = useCreateParameter({ setIsProcessRunning, setIsServiceError });
    const { mutate: deleteParameter } = useDeleteParameter();

    useEffect(() => {
        if (isDelete) {
            if (currOption.length - 1 === 4) {
                setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3], 0]);
            }
            if (!parameter.id) return;
            deleteParameter(parameter.id);
            setIsDelete(false);
        }
    }, [isDelete]); //eslint-disable-line

    const handleSave = (e: FormEvent) => {
        e.preventDefault();
        if (isEmptyCustom(parameterValue)) return;
        if (!parameter.id) {
            createParameter({ ...currentParameter, value: Array.isArray(parameterValue) ? JSON.stringify(parameterValue) : parameterValue.toString() });
        } else {
            updateParameter({ ...currentParameter, value: Array.isArray(parameterValue) ? JSON.stringify(parameterValue) : parameterValue.toString() });
        }
        setTitle(currentParameter.name || 'New Parameter');
    }

    const handleDelete = () => {
        if (!parameter.id) {
            setIsResetNew(true);
            if (currOption.length - 1 === 4) {
                setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3], 0]);
            }
        } else {
            setIsOpenDeleteModal(true);
            setDeleteModalMessage({ title: 'Delete Parameter', subtitle: 'Are you sure you want to delete this parameter?' });
        }
    }

    const handleMetaParameterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMetaParameterName = e.target.value;
        const newMetaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === newMetaParameterName);
        if (!newMetaParameter) return;
        const oldMetaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentParameter.name);

        setCurrentParameter({
            ...currentParameter,
            name: newMetaParameterName,
            value: newMetaParameter.type === ParameterType.ARRAY ? JSON.stringify(['']) : newMetaParameter.type === ParameterType.BOOLEAN ? '1' : '',
            description: (!currentParameter.description?.trim()?.length || currentParameter.description === oldMetaParameter?.description)
                ? newMetaParameter.description
                : currentParameter.description,
        });

        setParameterValue(newMetaParameter.type === ParameterType.ARRAY ? [''] : newMetaParameter.type === ParameterType.BOOLEAN ? '1' : '');
    }

    const renderValue = (type: string | undefined) => {
        if (!type) return null;

        switch (type) {
            case ParameterType.STRING:
                // setParameterValue('');
                return <input placeholder='Enter value' type='text' id='value' name='value' value={parameterValue ? parameterValue as string : ''} onChange={(e) => setParameterValue(e.target.value)} />;

            case ParameterType.BOOLEAN:
                return <select id='value' name='value' value={(parameterValue.toString() === '0' || 0) ? '0' : '1'} onChange={(e) => setParameterValue((e.target.value === '0' || 0) ? '0' : '1')}>
                    <option value={'1'}>True</option>
                    <option value={'0'}>False</option>
                </select>;

            case ParameterType.DATE:
                return <input type='date' id='value' name='value' value={parameterValue ? new Date(parameterValue as string).toDateString() : ''} onChange={(e) => setParameterValue(e.target.value)} />;

            case ParameterType.INT:
                return <input placeholder='Enter value' type='number' id='value' name='value' value={parameterValue ? parameterValue as number : ''} onChange={(e) => setParameterValue(parseInt(e.target.value))} />;

            case ParameterType.FLOAT:
                return <input placeholder='Enter value' type='number' id='value' name='value' value={parameterValue ? parameterValue as number : ''} onChange={(e) => setParameterValue(parseFloat(e.target.value))} />;

            case ParameterType.ARRAY:
                if (isArray && parameterValue) {
                    return parameterValue.map((value: string, index: number) => {

                        return (
                            <div className='field__action' key={index}>
                                <input placeholder='Enter value' type='text' id='value' name='value' value={value} onChange={(e) => setParameterValue([...parameterValue.slice(0, index), e.target.value, ...parameterValue.slice(index + 1)])} />
                                <button className='field__btn delete' type="button" onClick={() => setParameterValue([...parameterValue.slice(0, index), ...parameterValue.slice(index + 1)])}>
                                    <SVGIcon src={Icons.TrashFill} color='error' />
                                </button>
                            </div>
                        );
                    });
                } else return addValueField();

            default:
                return null;
        }
    }

    return (
        <div className='imm_admin__content'>
            <div className='content_head'>
                <h3>{title}</h3>
            </div>

            <form onSubmit={handleSave} className='content_form'>
                <div className='field'>
                    <label htmlFor='meta_parameter'>Meta Parameter <span className='field__required'>*</span></label>
                    <select id='meta_parameter' name='meta_parameter' value={currentParameter.name || -1} onChange={handleMetaParameterChange} >
                        <option value={-1} disabled>Select meta parameter</option>
                        {relatedMetaParameters.length > 0 && relatedMetaParameters.map(metaParameter => {
                            return <option key={metaParameter.name} value={metaParameter.name}>{metaParameter.name}</option>
                        })}

                        {relatedMetaParameters.length === 0 && <option value={-2} disabled>No Meta Parameters</option>}
                    </select>
                </div>

                <div className='field'>
                    <label htmlFor='meta_description'>Meta Description</label>
                    <textarea id='meta_description' name='meta_description' disabled placeholder='Enter Description' value={relatedMetaParameters?.find(metaParameter => metaParameter.name === currentParameter.name)?.description || ""}></textarea>
                </div>

                <div className='field'>
                    <label htmlFor='type'>Type</label>
                    <select disabled id='type' name='type' value={relatedMetaParameters?.find(metaParameter => metaParameter.name === currentParameter.name)?.type || ParameterType.DEFAULT} onChange={() => { }}>
                        <option value={ParameterType.DEFAULT} disabled>Select Type</option>
                        <option value={ParameterType.STRING}>String</option>
                        <option value={ParameterType.BOOLEAN}>Boolean</option>
                        <option value={ParameterType.DATE}>Date</option>
                        <option value={ParameterType.INT}>Int</option>
                        <option value={ParameterType.FLOAT}>Float</option>
                        <option value={ParameterType.ARRAY}>List</option>
                    </select>
                </div>

                <div className='field'>
                    <label htmlFor='description'>Description <span className='field__required'>*</span></label>
                    <textarea id='description' name='description' placeholder='Enter Description' value={currentParameter.description || ''} onChange={(e) => setCurrentParameter({ ...currentParameter, description: e.target.value })}></textarea>
                </div>

                {currentParameter ? (<div className='field'>
                    {relatedMetaParameters?.find(metaParameter => metaParameter.name === currentParameter.name)?.type === ParameterType.ARRAY ? <div className='field__action'>
                        <label htmlFor='value'>Value <span className='field__required'>*</span></label>
                        <button className='field__btn' type='button' onClick={addValueField}><SVGIcon src={Icons.Plus} /></button>
                    </div> : <label htmlFor='value'>Value <span className='field__required'>*</span></label>}

                    {renderValue(relatedMetaParameters?.find(metaParameter => metaParameter.name === currentParameter.name)?.type)}
                </div>) : null}

                <div className='actions'>
                    <button className='delete_btn' type="button" onClick={handleDelete} disabled={relatedMetaParameters.length === 0}>{!parameter.id ? 'Cancel' : 'Delete'}</button>
                    <button className='save_btn' type='submit' disabled={relatedMetaParameters.length === 0 || isEmptyCustom(parameterValue)} >{isProcessRunning ? <i className='imm_admin__process_loader' /> : !parameter.id ? 'Create' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
};

export default Parameter;